import { useEffect, useRef, useState } from "react";

import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router";

import { LinkIcon } from "lucide-react";

import { styled } from "simplydo/react/styled";
import { Icon } from "simplydo/react/ui/icon";

import util from "utils/utils";

const HEADING_SELECTOR = "h1, h2, h3, h4, h5, h6";

const Wrapper = styled.div`
  position: relative;

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    scroll-margin-top: 80px;
  }
`;

const AnchorButton = styled.button`
  position: absolute;
  left: -28px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  padding: 0;
  border: none;
  background: none;
  color: #a0a0a0;
  cursor: pointer;

  &:hover,
  &:focus-visible {
    color: ${({ theme }) => theme.primaryColour};
  }
`;

const slugify = (text: string) =>
  text
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .trim()
    .replace(/[^\p{L}\p{N}]+/gu, "-")
    .replace(/^-+|-+$/g, "");

// Maps each heading to its slug, suffixed with a counter if an earlier heading
// on the page has the same text (e.g. `results`, `results-2`)
const getHeadingSlugs = (container: HTMLElement) => {
  const slugs = new Map<Element, string>();
  const occurrences = new Map<string, number>();
  for (const heading of container.querySelectorAll(HEADING_SELECTOR)) {
    const slug = slugify(heading.textContent ?? "");
    if (!slug) continue;
    const occurrence = (occurrences.get(slug) ?? 0) + 1;
    occurrences.set(slug, occurrence);
    slugs.set(heading, occurrence > 1 ? `${slug}-${occurrence}` : slug);
  }
  return slugs;
};

const findHeadingBySlug = (container: HTMLElement, slug: string) => {
  for (const [heading, headingSlug] of getHeadingSlugs(container)) {
    if (headingSlug === slug) return heading;
  }
  return undefined;
};

const decodeHash = (hash: string) => {
  const raw = hash.replace(/^#/, "");
  try {
    return decodeURIComponent(raw);
  } catch {
    // Malformed percent-encoding (e.g. `#%E0%A4%A`)
    return raw;
  }
};

type HoveredHeading = { element: Element; top: number; height: number };

type HeadingAnchorsProps = { children: React.ReactNode };

/**
 * Shows a link button next to any heading inside `children` on hover. Clicking
 * it copies a URL with a `#heading-slug` hash; loading a URL with such a hash
 * scrolls to the matching heading.
 *
 * Headings are matched by their text rather than by an `id` attribute so the
 * rendered rich text DOM does not need to be modified.
 */
export const HeadingAnchors = ({ children }: HeadingAnchorsProps) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [hovered, setHovered] = useState<HoveredHeading | null>(null);

  useEffect(() => {
    const wrapper = wrapperRef.current;
    const slug = decodeHash(location.hash);

    if (!wrapper || !slug) return;
    findHeadingBySlug(wrapper, slug)?.scrollIntoView({ block: "start" });
  }, [location.hash]);

  const handleMouseOver = (event: React.MouseEvent) => {
    const wrapper = wrapperRef.current;
    const target = event.target as Element;
    if (!wrapper) return;
    // Keep the current heading while moving onto the anchor button itself
    if (target.closest("[data-heading-anchor]")) return;

    const heading = target.closest(HEADING_SELECTOR);
    if (!heading || !wrapper.contains(heading) || !heading.textContent) {
      setHovered(null);
      return;
    }
    if (heading === hovered?.element) return;
    const wrapperRect = wrapper.getBoundingClientRect();
    const headingRect = heading.getBoundingClientRect();
    setHovered({
      element: heading,
      top: headingRect.top - wrapperRect.top,
      height: headingRect.height,
    });
  };

  const copyLink = () => {
    const wrapper = wrapperRef.current;
    if (!wrapper || !hovered) return;
    const slug = getHeadingSlugs(wrapper).get(hovered.element);
    if (!slug) return;
    const url = new URL(window.location.href);
    url.hash = slug;
    util.copyToClipboard(url.toString());
    // Go through the router so `location.hash` stays in sync with the address
    // bar. `search` must be passed explicitly or it is dropped.
    navigate({ search: location.search, hash: slug }, { replace: true });
  };

  return (
    <Wrapper
      ref={wrapperRef}
      onMouseOver={handleMouseOver}
      onMouseLeave={() => setHovered(null)}
    >
      {children}
      {hovered ? (
        <AnchorButton
          type="button"
          data-heading-anchor
          aria-label={t("customPage.copyHeadingLink")}
          title={t("customPage.copyHeadingLink")}
          onClick={copyLink}
          style={{ top: hovered.top, height: hovered.height }}
        >
          <Icon icon={LinkIcon} />
        </AnchorButton>
      ) : null}
    </Wrapper>
  );
};
